import { extractValidationErrorMessage } from '../../helpers/api-error-helpers';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type {
  AutomationWorkflow,
  AutomationWorkflowRun,
  AutomationWorkflowWrite,
  AutomationEmailRecipientSuggestionsResponse,
  FilterDefinitionsResponse,
} from './automations-workflows-client.type';

const axiosInstance = AxiosInstance();
const baseURL = `${appConfig.VITE_APP_API_URL}/api/automations/workflows/`;

/**
 * Lists all automation workflows owned by the current user inside the given organization.
 *
 * @param {string} organizationId - The organization UUID; required by the BE
 * @returns {Promise<AutomationWorkflow[]>} List of workflows in reverse-chronological order
 */
function getAutomationWorkflows(organizationId: string): Promise<AutomationWorkflow[]> {
  return axiosInstance
    .get<AutomationWorkflow[]>(baseURL, { params: { organization_id: organizationId } })
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch automation workflows';
      console.error('Error fetching automation workflows:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Retrieves a single automation workflow by id.
 *
 * @param {string} id - The workflow UUID
 * @returns {Promise<AutomationWorkflow>} The workflow record
 */
function getAutomationWorkflow(id: string): Promise<AutomationWorkflow> {
  return axiosInstance
    .get<AutomationWorkflow>(`${baseURL}${id}/`)
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch automation workflow';
      console.error('Error fetching automation workflow:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Creates a new automation workflow.
 *
 * @param {AutomationWorkflowWrite} data - Full workflow payload (BE validates trigger consistency)
 * @returns {Promise<AutomationWorkflow>} The created workflow
 */
function createAutomationWorkflow(data: AutomationWorkflowWrite): Promise<AutomationWorkflow> {
  return axiosInstance
    .post<AutomationWorkflow>(baseURL, data)
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = extractValidationErrorMessage(error, 'Failed to create automation workflow');
      console.error('Error creating automation workflow:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Partially updates an existing automation workflow.
 *
 * @param {string} id - The workflow UUID
 * @param {Partial<AutomationWorkflowWrite>} data - Fields to update
 * @returns {Promise<AutomationWorkflow>} The updated workflow
 */
function updateAutomationWorkflow(id: string, data: Partial<AutomationWorkflowWrite>): Promise<AutomationWorkflow> {
  return axiosInstance
    .patch<AutomationWorkflow>(`${baseURL}${id}/`, data)
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = extractValidationErrorMessage(error, 'Failed to update automation workflow');
      console.error('Error updating automation workflow:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Deletes an automation workflow.
 *
 * @param {string} id - The workflow UUID
 * @returns {Promise<void>} Resolves once the BE acknowledges the 204
 */
interface RunAutomationWorkflowResponse {
  run_id: string;
  status: string;
}

/**
 * Triggers a manual run of an automation workflow. Only MANUAL and CRON trigger types are eligible — EVENT workflows reject the request.
 *
 * @param {string} id - The workflow UUID
 * @returns {Promise<RunAutomationWorkflowResponse>} The queued run acknowledgement
 */
function runAutomationWorkflow(id: string): Promise<RunAutomationWorkflowResponse> {
  return axiosInstance
    .post<RunAutomationWorkflowResponse>(`${baseURL}${id}/run/`)
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to run automation';
      console.error('Error running automation workflow:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Deletes an automation workflow.
 *
 * @param {string} id - The workflow UUID
 * @returns {Promise<void>} Resolves once the BE acknowledges the 204
 */
function deleteAutomationWorkflow(id: string): Promise<void> {
  return axiosInstance
    .delete(`${baseURL}${id}/`)
    .then(() => undefined)
    .catch((error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete automation workflow';
      console.error('Error deleting automation workflow:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Lists the run history for an automation workflow, newest first.
 *
 * @param {string} id - The workflow UUID
 * @returns {Promise<AutomationWorkflowRun[]>} The workflow's runs in reverse-chronological order
 */
function getAutomationWorkflowRuns(id: string): Promise<AutomationWorkflowRun[]> {
  return axiosInstance
    .get<AutomationWorkflowRun[]>(`${baseURL}${id}/runs/`)
    .then((response) => response.data)
    .catch((error) => {
      // DRF 404s carry `detail`, not `message`; fall through both before the generic copy.
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch automation runs';
      console.error('Error fetching automation runs:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Fetches the catalog of supported events plus their filter-field definitions.
 *
 * The active organization is sent so the BE can scope organization-restricted
 * internal triggers (e.g. Flora Debug Error) to their allow-listed orgs.
 *
 * @param {string} organizationId - The active organization UUID; required by the BE
 * @returns {Promise<FilterDefinitionsResponse>} The events array used to drive the trigger picker
 */
function getAutomationFilterDefinitions(organizationId: string): Promise<FilterDefinitionsResponse> {
  return axiosInstance
    .get<FilterDefinitionsResponse>(`${baseURL}filter-definitions/`, {
      params: { organization_id: organizationId },
    })
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch filter definitions';
      console.error('Error fetching filter definitions:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Validates the minimal response contract used by the email recipient picker.
 *
 * @param {unknown} data - Untrusted API response data
 * @returns {boolean} Whether data contains only name/email suggestion records
 */
function isAutomationEmailRecipientSuggestionsResponse(
  data: unknown,
): data is AutomationEmailRecipientSuggestionsResponse {
  if (typeof data !== 'object' || data === null || !('results' in data)) return false;

  const { results } = data as { results?: unknown };
  return (
    Array.isArray(results) &&
    results.every((item) => {
      if (typeof item !== 'object' || item === null) return false;
      const suggestion = item as Record<string, unknown>;
      return typeof suggestion.name === 'string' && typeof suggestion.email === 'string';
    })
  );
}

/**
 * Searches active members of an organization for email delivery recipients.
 *
 * @param {string} organizationId - The active organization UUID
 * @param {string} search - Case-insensitive name or email search text
 * @param {number} limit - Maximum number of suggestions to return
 * @returns {Promise<AutomationEmailRecipientSuggestionsResponse>} Matching members with minimal display fields
 */
function getAutomationEmailRecipientSuggestions(
  organizationId: string,
  search: string,
  limit = 20,
): Promise<AutomationEmailRecipientSuggestionsResponse> {
  return axiosInstance
    .get<AutomationEmailRecipientSuggestionsResponse>(`${baseURL}email-recipient-suggestions/`, {
      params: { organization_id: organizationId, search, limit },
    })
    .then((response) => {
      const data: unknown = response.data;
      if (!isAutomationEmailRecipientSuggestionsResponse(data)) {
        throw new Error('Email recipient search returned malformed data');
      }
      return data;
    })
    .catch((error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch email recipients';
      console.error('Error fetching automation email recipient suggestions:', errorMessage);
      throw new Error(errorMessage);
    });
}

export {
  createAutomationWorkflow,
  deleteAutomationWorkflow,
  getAutomationEmailRecipientSuggestions,
  getAutomationFilterDefinitions,
  getAutomationWorkflow,
  getAutomationWorkflowRuns,
  getAutomationWorkflows,
  runAutomationWorkflow,
  updateAutomationWorkflow,
};
export type { RunAutomationWorkflowResponse };
